import React, { useState, useMemo, useCallback } from "react";
import { useProcurementUser } from "../context/ProcurementUserContext";
import ProcurementUserSwitcher from "../components/ProcurementUserSwitcher";

const API = process.env.REACT_APP_API_URL || "";
const API_KEY = process.env.REACT_APP_API_KEY || "";

const C = {
  bg: "#0F1117", card: "#161C2E", cardBorder: "#222A40",
  gold: "#C9972B", text: "#F4F0E8", textSec: "#9EA8C0", textDim: "#5C657A",
  red: "#EF4444", green: "#22C55E", amber: "#F59E0B", blue: "#3B82F6",
  teal: "#14B8A6", orange: "#F97316", rule: "#2A3048",
};

const STATUS_COLORS = {
  not_started: "#64748B", in_production: C.blue, ready_to_ship: C.amber,
  shipped: C.orange, in_storage: "#8B5CF6", delivered: C.teal,
  installed: C.green, commissioned: "#22D3EE", ops_turnover: "#10B981",
};
const ROJ_COLORS = { pfr: C.blue, gc: C.green, manual: "#64748B" };
const ALL_STATUSES = Object.keys(STATUS_COLORS);
const PAGE_SIZE = 50;

const today = new Date(); today.setHours(0,0,0,0);
const in14 = new Date(today); in14.setDate(in14.getDate() + 14);

function dateUrgency(d) {
  if (!d) return null;
  const dt = new Date(d); dt.setHours(0,0,0,0);
  if (dt < today) return "past";
  if (dt <= in14) return "soon";
  return "ok";
}

function Badge({ text, color }) {
  return <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, fontFamily: "monospace", background: `${color}22`, color, border: `1px solid ${color}44` }}>{text?.replace(/_/g, " ")}</span>;
}
function Toast({ msg, onClose }) {
  if (!msg) return null;
  return <div style={{ position: "fixed", bottom: 24, right: 24, background: C.green, color: "#fff", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontFamily: "monospace", zIndex: 999, boxShadow: "0 4px 20px rgba(0,0,0,0.4)" }}>{msg}<button onClick={onClose} style={{ marginLeft: 12, background: "none", border: "none", color: "#fff", cursor: "pointer", fontWeight: 700 }}>x</button></div>;
}

export default function ProjectSchedule() {
  const { activeUser } = useProcurementUser();
  const hdrs = { "Content-Type": "application/json", ...(API_KEY ? { "X-API-Key": API_KEY } : {}), "X-User": activeUser.identifier };

  const [allocation, setAllocation] = useState("");
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [toast, setToast] = useState(null);
  const [filters, setFilters] = useState({ phase: "", vendor: "", status: "", pastDueOnly: false });
  const [sortCol, setSortCol] = useState("contractual_date");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(0);

  // Edit modal state
  const [eRoj, setERoj] = useState("");
  const [eRojSrc, setERojSrc] = useState("manual");
  const [eIntNeed, setEIntNeed] = useState("");
  const [eContainer, setEContainer] = useState("");
  const [eStorAddr, setEStorAddr] = useState("");
  const [eOutStor, setEOutStor] = useState("");
  const [eDelConst, setEDelConst] = useState("");

  const loadSchedule = useCallback(async () => {
    if (!allocation.trim()) return;
    setLoading(true); setError(null); setSchedule(null); setPage(0);
    try {
      const r = await fetch(`${API}/procurement/schedule/${encodeURIComponent(allocation.trim())}`, { headers: hdrs });
      const d = await r.json();
      setSchedule(d.schedule || []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [allocation]);

  const syncFromPlanner = async () => {
    setSyncing(true);
    try {
      const r = await fetch(`${API}/procurement/schedule/${encodeURIComponent(allocation.trim())}/sync`, { method: "POST", headers: hdrs });
      const d = await r.json();
      setToast(`Synced ${d.rows_synced} rows from planner`);
      loadSchedule();
    } catch (e) { setError(e.message); }
    setSyncing(false);
    setTimeout(() => setToast(null), 3000);
  };

  const openEdit = (row) => {
    setEditRow(row);
    setERoj(row.roj_execution || "");
    setERojSrc(row.roj_source || "manual");
    setEIntNeed(row.integrator_need || "");
    setEContainer(row.container_skid_num || "");
    setEStorAddr(row.storage_address || "");
    setEOutStor(row.out_of_storage_date || "");
    setEDelConst(row.delivery_to_construction || "");
  };

  const submitEdit = async () => {
    const body = {};
    if (eRoj) body.roj_execution = eRoj;
    if (eRojSrc) body.roj_source = eRojSrc;
    if (eIntNeed) body.integrator_need = eIntNeed;
    if (eContainer) body.container_skid_num = eContainer;
    if (eStorAddr) body.storage_address = eStorAddr;
    if (eOutStor) body.out_of_storage_date = eOutStor;
    if (eDelConst) body.delivery_to_construction = eDelConst;
    await fetch(`${API}/procurement/schedule/${editRow.id}`, { method: "PATCH", headers: hdrs, body: JSON.stringify(body) });
    setEditRow(null); setToast("Schedule row updated"); loadSchedule();
    setTimeout(() => setToast(null), 3000);
  };

  const handleSort = (col) => { if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortCol(col); setSortDir("asc"); } setPage(0); };

  const phases = useMemo(() => [...new Set((schedule || []).map(r => r.build_phase_seq).filter(Boolean))].sort(), [schedule]);
  const vendors = useMemo(() => [...new Set((schedule || []).map(r => r.vendor_name).filter(Boolean))].sort(), [schedule]);

  const filteredRows = useMemo(() => {
    let rows = schedule || [];
    if (filters.phase) rows = rows.filter(r => r.build_phase_seq === filters.phase);
    if (filters.vendor) rows = rows.filter(r => r.vendor_name === filters.vendor);
    if (filters.status) rows = rows.filter(r => r.production_status === filters.status);
    if (filters.pastDueOnly) rows = rows.filter(r => dateUrgency(r.contractual_date) === "past");
    return rows;
  }, [schedule, filters]);

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      let av = a[sortCol], bv = b[sortCol];
      if (av == null) return 1; if (bv == null) return -1;
      if (typeof av === "string" && typeof bv === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? (av - bv) : (bv - av);
    });
  }, [filteredRows, sortCol, sortDir]);

  const pagedRows = useMemo(() => sortedRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [sortedRows, page]);
  const totalPages = Math.ceil(sortedRows.length / PAGE_SIZE);

  const stats = useMemo(() => {
    const rows = schedule || [];
    return {
      total: rows.length,
      pastDue: rows.filter(r => dateUrgency(r.contractual_date) === "past").length,
      dueSoon: rows.filter(r => dateUrgency(r.contractual_date) === "soon").length,
      delivered: rows.filter(r => ["delivered","installed","commissioned","ops_turnover"].includes(r.production_status)).length,
    };
  }, [schedule]);

  const si = (col) => sortCol === col ? (sortDir === "asc" ? " \u2191" : " \u2193") : " \u2195";
  const th = { padding: "8px 10px", fontSize: 11, fontFamily: "monospace", textTransform: "uppercase", color: C.textDim, cursor: "pointer", whiteSpace: "nowrap", borderBottom: `1px solid ${C.rule}`, textAlign: "left", letterSpacing: "0.06em" };
  const td = { padding: "6px 8px", fontSize: 12, borderBottom: `1px solid ${C.rule}`, fontFamily: "monospace" };
  const inp = { background: C.bg, border: `1px solid ${C.cardBorder}`, color: C.text, borderRadius: 8, padding: "6px 10px", fontSize: 12, fontFamily: "monospace", outline: "none" };
  const sel = { ...inp, cursor: "pointer" };
  const btn = { background: C.gold, color: C.bg, border: "none", borderRadius: 8, padding: "6px 16px", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "monospace" };

  const dateCell = (val, urgency) => {
    if (!val) return <span style={{ color: C.textDim }}>—</span>;
    const u = urgency || dateUrgency(val);
    const color = u === "past" ? C.red : u === "soon" ? C.amber : C.text;
    return <span style={{ color }}>{val}</span>;
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Outfit', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 1600, margin: "0 auto", padding: "16px 24px 48px" }}>

        {/* HEADER */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, borderBottom: `1px solid ${C.rule}`, paddingBottom: 12 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Project Schedule</h1>
            {schedule && <div style={{ fontSize: 12, color: C.textSec }}>{stats.total} items | {allocation}</div>}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <ProcurementUserSwitcher />
            {schedule && allocation && <button onClick={syncFromPlanner} disabled={syncing} style={{ ...btn, background: syncing ? C.textDim : C.amber }}>{syncing ? "Syncing..." : "Sync from Planner"}</button>}
            <button onClick={loadSchedule} style={{ ...btn, background: C.card, color: C.textSec, border: `1px solid ${C.cardBorder}` }}>Refresh</button>
          </div>
        </div>

        {/* ALLOCATION SELECTOR */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
          <input value={allocation} onChange={e => setAllocation(e.target.value)} placeholder="Enter allocation (e.g. DEC3.0-PHX-B1)" onKeyDown={e => e.key === "Enter" && loadSchedule()}
            style={{ ...inp, flex: 1, maxWidth: 400 }} />
          <button onClick={loadSchedule} disabled={loading || !allocation.trim()} style={btn}>{loading ? "Loading..." : "Load"}</button>
        </div>

        {error && <div style={{ color: C.red, marginBottom: 12, fontSize: 13 }}>{error}</div>}

        {/* SYNC PANEL */}
        {schedule && allocation && (
          <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: "12px 20px", marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 12, color: C.textSec, flex: 1 }}>Schedule rows auto-populated from Production Planner. Click Sync to refresh from current planner state.</span>
            <button onClick={syncFromPlanner} disabled={syncing} style={{ ...btn, background: syncing ? C.textDim : C.amber, padding: "6px 14px" }}>{syncing ? "Syncing..." : "Sync from Planner"}</button>
          </div>
        )}

        {/* NOT LOADED */}
        {!schedule && !loading && !error && (
          <div style={{ textAlign: "center", padding: "80px 0", color: C.textDim }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>&#x1F4C5;</div>
            <div style={{ fontSize: 15 }}>Enter a project allocation above to load schedule.</div>
          </div>
        )}

        {/* EMPTY AFTER LOAD */}
        {schedule && schedule.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 0", color: C.textDim }}>
            <div style={{ fontSize: 15 }}>No schedule rows found for <strong style={{ color: C.gold }}>{allocation}</strong>.</div>
            <div style={{ fontSize: 12, marginTop: 6, marginBottom: 12 }}>Run Sync from Planner to populate schedule data.</div>
            <button onClick={syncFromPlanner} disabled={syncing} style={btn}>{syncing ? "Syncing..." : "Sync from Planner"}</button>
          </div>
        )}

        {/* LOADED WITH DATA */}
        {schedule && schedule.length > 0 && (
          <>
            {/* SUMMARY */}
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              {[
                { label: "Total Items", value: stats.total, color: C.text },
                { label: "Past Due", value: stats.pastDue, color: stats.pastDue > 0 ? C.red : C.green },
                { label: "Due \u226414 Days", value: stats.dueSoon, color: stats.dueSoon > 0 ? C.amber : C.text },
                { label: "Delivered+", value: stats.delivered, color: C.teal },
              ].map((s, i) => (
                <div key={i} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: "12px 20px", minWidth: 140 }}>
                  <div style={{ fontSize: 10, fontFamily: "monospace", textTransform: "uppercase", color: C.textDim, marginBottom: 2 }}>{s.label}</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: s.color, fontFamily: "monospace" }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* FILTERS */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
              <select value={filters.phase} onChange={e => { setFilters(f => ({ ...f, phase: e.target.value })); setPage(0); }} style={sel}>
                <option value="">All Phases</option>
                {phases.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={filters.vendor} onChange={e => { setFilters(f => ({ ...f, vendor: e.target.value })); setPage(0); }} style={sel}>
                <option value="">All Vendors</option>
                {vendors.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <select value={filters.status} onChange={e => { setFilters(f => ({ ...f, status: e.target.value })); setPage(0); }} style={sel}>
                <option value="">All Statuses</option>
                {ALL_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
              <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: C.textSec, cursor: "pointer" }}>
                <input type="checkbox" checked={filters.pastDueOnly} onChange={e => { setFilters(f => ({ ...f, pastDueOnly: e.target.checked })); setPage(0); }} />
                Past due only
              </label>
              <button onClick={() => setFilters({ phase: "", vendor: "", status: "", pastDueOnly: false })} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>Clear</button>
              <span style={{ fontSize: 11, color: C.textDim, fontFamily: "monospace" }}>{sortedRows.length} rows | Page {page + 1}/{totalPages || 1}</span>
            </div>

            {/* TABLE */}
            <div style={{ overflowX: "auto", borderRadius: 12, border: `1px solid ${C.cardBorder}` }}>
              <table style={{ width: "100%", borderCollapse: "collapse", background: C.card }}>
                <thead>
                  <tr>
                    <th style={th}>Status</th>
                    <th style={th} onClick={() => handleSort("allocation")}>Alloc{si("allocation")}</th>
                    <th style={th} onClick={() => handleSort("build_phase_seq")}>Phase{si("build_phase_seq")}</th>
                    <th style={th} onClick={() => handleSort("equipment_type")}>Equip{si("equipment_type")}</th>
                    <th style={th} onClick={() => handleSort("vendor_name")}>Vendor{si("vendor_name")}</th>
                    <th style={th}>SKU</th>
                    <th style={th}>PO Line</th>
                    <th style={th} onClick={() => handleSort("contractual_date")}>Contract Date{si("contractual_date")}</th>
                    <th style={th}>ROJ Exec</th>
                    <th style={th}>Int. Need</th>
                    <th style={th}>Container#</th>
                    <th style={th}>MFG Loc</th>
                    <th style={th}>MFG Plan</th>
                    <th style={th}>MFG Actual</th>
                    <th style={th}>Del. Dest</th>
                    <th style={th}>Storage</th>
                    <th style={th}>Out Storage</th>
                    <th style={th}>To Constr.</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((r, i) => {
                    const u = dateUrgency(r.contractual_date);
                    const isPast = u === "past";
                    const lateShip = r.mfg_ship_date_actual && r.mfg_ship_date_plan && r.mfg_ship_date_actual > r.mfg_ship_date_plan;
                    return (
                      <tr key={r.id || i} style={{ borderLeft: isPast ? `3px solid ${C.red}` : "3px solid transparent", background: isPast ? `${C.red}06` : "transparent" }}>
                        <td style={td}><Badge text={r.production_status} color={STATUS_COLORS[r.production_status] || "#64748B"} /></td>
                        <td style={td}>{r.allocation}</td>
                        <td style={td}>{r.build_phase_seq}</td>
                        <td style={td}>{r.equipment_type}</td>
                        <td style={{ ...td, color: C.textSec }}>{r.vendor_name}</td>
                        <td style={td}>{r.sku}</td>
                        <td style={td}>{r.po_line}</td>
                        <td style={td}>{dateCell(r.contractual_date, u)}</td>
                        <td style={td}>{r.roj_execution ? <>{r.roj_execution} <Badge text={r.roj_source || "manual"} color={ROJ_COLORS[r.roj_source] || "#64748B"} /></> : "—"}</td>
                        <td style={td}>{r.integrator_need || "—"}</td>
                        <td style={td}>{r.container_skid_num || "—"}</td>
                        <td style={td}>{r.mfg_location || "—"}</td>
                        <td style={td}>{r.mfg_ship_date_plan || "—"}</td>
                        <td style={{ ...td, background: lateShip ? `${C.orange}15` : "transparent" }}><span style={{ color: lateShip ? C.orange : C.text }}>{r.mfg_ship_date_actual || "—"}</span></td>
                        <td style={td}>{r.delivery_first_dest || "—"}</td>
                        <td style={td}>{r.storage_facility || "—"}</td>
                        <td style={td}>{r.out_of_storage_date ? <span style={{ color: C.teal }}>{r.out_of_storage_date}</span> : "—"}</td>
                        <td style={td}>{r.delivery_to_construction || "—"}</td>
                        <td style={td}><button onClick={() => openEdit(r)} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 4, padding: "2px 8px", fontSize: 10, cursor: "pointer", fontFamily: "monospace" }}>Edit ROJ</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            {totalPages > 1 && (
              <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 12 }}>
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, color: C.textSec, borderRadius: 6, padding: "4px 12px", fontSize: 12, cursor: "pointer" }}>&laquo; Prev</button>
                <span style={{ color: C.textDim, fontSize: 12, padding: "4px 8px", fontFamily: "monospace" }}>{page + 1} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, color: C.textSec, borderRadius: 6, padding: "4px 12px", fontSize: 12, cursor: "pointer" }}>Next &raquo;</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* EDIT ROJ MODAL */}
      {editRow && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 900 }} onClick={() => setEditRow(null)}>
          <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 28, width: 440, maxWidth: "90vw" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Update Schedule</div>
            <div style={{ fontSize: 12, color: C.textSec, marginBottom: 16, fontFamily: "monospace" }}>{editRow.equipment_type} | Row #{editRow.id}</div>
            {[
              ["ROJ Execution", eRoj, setERoj, "date"],
              ["Integrator Need", eIntNeed, setEIntNeed, "date"],
              ["Container / Skid #", eContainer, setEContainer, "text"],
              ["Storage Address", eStorAddr, setEStorAddr, "text"],
              ["Out of Storage Date", eOutStor, setEOutStor, "date"],
              ["Delivery to Construction", eDelConst, setEDelConst, "date"],
            ].map(([label, val, setter, type]) => (
              <div key={label} style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>{label}</label>
                <input type={type} value={val} onChange={e => setter(e.target.value)} style={{ ...inp, width: "100%", boxSizing: "border-box" }} />
              </div>
            ))}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>ROJ Source</label>
              <select value={eRojSrc} onChange={e => setERojSrc(e.target.value)} style={{ ...sel, width: "100%", boxSizing: "border-box" }}>
                <option value="pfr">PFR</option>
                <option value="gc">GC</option>
                <option value="manual">Manual</option>
              </select>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setEditRow(null)} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 8, padding: "6px 16px", fontSize: 12, cursor: "pointer" }}>Cancel</button>
              <button onClick={submitEdit} style={btn}>Save</button>
            </div>
          </div>
        </div>
      )}

      <Toast msg={toast} onClose={() => setToast(null)} />
    </div>
  );
}
